import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { ArrowRight, Loader2, AlertTriangle, CheckCircle, User } from 'lucide-react';
import { toast } from 'sonner';

const DeskShiftDialog = ({ open, onOpenChange, application, desks, onSuccess }) => {
  const { api } = useAuth();
  const [targetDesk, setTargetDesk] = useState('');
  const [deskHandler, setDeskHandler] = useState('');
  const [notes, setNotes] = useState('');
  const [shifting, setShifting] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const currentDeskData = desks.find(d => d.slug === application?.current_desk);
  const allowedTransitions = currentDeskData?.transitions || [];

  // Build list of available target desks
  const availableDesks = desks.filter(d => 
    allowedTransitions.includes(d.slug) || d.slug === 'status'
  );

  // Fetch users when dialog opens or target desk changes
  useEffect(() => {
    const fetchUsers = async () => {
      if (!open) return;
      setLoadingUsers(true);
      try {
        const response = await api().get('/users');
        setUsers(response.data || []);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, [open, api]);

  // Filter users based on target desk (show users with matching role)
  const getFilteredUsers = () => {
    if (!targetDesk || !users.length) return users;
    
    // Find the target desk name
    const targetDeskObj = desks.find(d => d.slug === targetDesk);
    const targetDeskName = targetDeskObj?.name?.toLowerCase() || targetDesk.toLowerCase();
    
    // Filter users who have a role matching the target desk
    // e.g., if target desk is "admission", show users with "Admission" related roles
    return users.filter(user => {
      // Show all users but prioritize those with matching roles
      return true; // Show all users for flexibility
    });
  };

  const handleShift = async () => {
    if (!targetDesk) {
      toast.error('Please select a target desk');
      return;
    }

    if (!deskHandler) {
      toast.error('Please select a user to forward the application to');
      return;
    }

    setShifting(true);
    try {
      await api().post(`/applications/${application.id}/shift-desk`, {
        target_desk: targetDesk,
        desk_handler: deskHandler,
        notes: notes || null
      });
      
      const handlerName = users.find(u => u.id === deskHandler)?.name || 'selected user';
      toast.success(`Application forwarded to ${handlerName}`);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to shift desk');
    } finally {
      setShifting(false);
    }
  };

  const handleClose = () => {
    setTargetDesk('');
    setDeskHandler('');
    setNotes('');
    onOpenChange(false);
  };

  if (!application) return null;

  const filteredUsers = getFilteredUsers();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRight className="w-5 h-5" />
            Forward Application
          </DialogTitle>
          <DialogDescription>
            Forward this application to another user's desk
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current State */}
          <div className="p-3 rounded-lg bg-muted/50">
            <p className="text-sm text-muted-foreground">Current Desk</p>
            <p className="font-medium">{currentDeskData?.name || application.current_desk}</p>
            {application.current_desk_handler_name && (
              <p className="text-sm text-muted-foreground mt-1">
                Handler: {application.current_desk_handler_name}
              </p>
            )}
          </div>

          {/* Target Desk Selection */}
          <div className="space-y-2">
            <Label>Target Desk <span className="text-red-500">*</span></Label>
            {availableDesks.length > 0 ? (
              <Select value={targetDesk} onValueChange={(val) => { setTargetDesk(val); setDeskHandler(''); }}>
                <SelectTrigger data-testid="shift-desk-select">
                  <SelectValue placeholder="Select target desk" />
                </SelectTrigger>
                <SelectContent>
                  {availableDesks.map(desk => (
                    <SelectItem key={desk.slug} value={desk.slug}>
                      {desk.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  No transitions available from the current desk.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Forward To User Selection */}
          {targetDesk && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Forward To <span className="text-red-500">*</span>
              </Label>
              {loadingUsers ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading users...
                </div>
              ) : (
                <Select value={deskHandler} onValueChange={setDeskHandler}>
                  <SelectTrigger data-testid="desk-handler-select">
                    <SelectValue placeholder="Select user to forward to" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <span>{user.name}</span>
                          <span className="text-muted-foreground text-xs">({user.email})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-muted-foreground">
                The selected user will receive this application in their Pending Review tab
              </p>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this transfer..."
              rows={3}
              data-testid="shift-notes"
            />
          </div>

          {/* Transition Arrow */}
          {targetDesk && deskHandler && (
            <div className="flex items-center justify-center gap-3 py-2 px-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">From</p>
                <p className="text-sm font-medium">{currentDeskData?.name}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-green-600" />
              <div className="text-center">
                <p className="text-xs text-muted-foreground">To</p>
                <p className="text-sm font-medium text-green-700 dark:text-green-400">
                  {users.find(u => u.id === deskHandler)?.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  ({desks.find(d => d.slug === targetDesk)?.name})
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleShift} 
            disabled={!targetDesk || !deskHandler || shifting}
            data-testid="confirm-shift-btn"
          >
            {shifting ? (
              <span className="flex items-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Forwarding...
              </span>
            ) : (
              <span className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Forward Application
              </span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeskShiftDialog;
